import {ChevronRight} from 'lucide-react';
import {trpc} from '@common-web/lib/trpc';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {DataTable, type Column} from '@common-web/components/data-table';
import type {RouterOutput} from '@common-web/lib/trpc';
import {
  ActiveFilterPills,
  ConsolidatedFilterBar,
} from '@internal/pages/claims/ConsolidatedFilterBar';
import {useConsolidatedFilters} from '@internal/pages/claims/useConsolidatedFilters';

type Row = RouterOutput['internal']['claimsTracking']['consolidated']['getExCodeSummary'][number];

export function ExCodesTab() {
  const {filters, drillToClaims} = useConsolidatedFilters();
  const {data, isLoading, isError, error} =
    trpc.internal.claimsTracking.consolidated.getExCodeSummary.useQuery(filters);

  const columns: Column<Row>[] = [
    {
      id: 'exCode',
      header: 'Ex code',
      render: (row) => <span className='font-mono text-sm text-brand-navy'>{row.exCode}</span>,
    },
    {
      id: 'description',
      header: 'Description',
      render: (row) => (
        <span className='text-sm text-muted-foreground'>{row.description ?? '—'}</span>
      ),
    },
    {
      id: 'claimCount',
      header: 'Claims',
      align: 'right',
      render: (row) => (
        <button
          type='button'
          onClick={() => drillToClaims({exCode: row.exCode})}
          className='font-mono tabular-nums text-sm text-primary hover:underline flex items-center gap-0.5 ml-auto'
        >
          {row.claimCount.toLocaleString()}
          <ChevronRight className='h-3.5 w-3.5 shrink-0' />
        </button>
      ),
    },
  ];

  return (
    <div className='space-y-4'>
      <Card>
        <CardContent className='pt-6 space-y-3'>
          <div className='flex items-end gap-3 flex-wrap'>
            <ConsolidatedFilterBar />
          </div>
          <ActiveFilterPills />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className='pb-3'>
          <CardTitle className='text-base'>Claims by ex code</CardTitle>
        </CardHeader>
        <CardContent>
          <p className='text-sm text-muted-foreground mb-4'>
            Distinct claims carrying each uncleared ex code (header or line). Click a count to view
            those claims.
          </p>
          <DataTable<Row>
            columns={columns}
            data={data}
            loading={isLoading}
            error={error}
            emptyMessage='No open ex codes for these filters.'
            rowKey={(row) => row.exCode}
          />
        </CardContent>
      </Card>
    </div>
  );
}
